import type { DOMProps } from "@simplybusiness/mobius";
import { useBreakpoint } from "@simplybusiness/mobius";
import type { RefAttributes } from "react";
import {
  useCurrentSection,
  useQuestionnaire,
  useSections,
} from "../../../../features/questionnaire";
import { useShouldBlockSubmission } from "../../../../hooks/useShouldBlockSubmission";
import { QuestionnaireActions } from "../QuestionnaireActions";
import type { ActionsElement, BaseQuestionnaireActionsProps } from "../index";

export type StandardQuestionnaireActionsProps = BaseQuestionnaireActionsProps &
  DOMProps &
  RefAttributes<ActionsElement>;

/**
 * Standard questionnaire actions composition for multi-page flows.
 * Renders Back, Next, or Submit actions based on the current section state.
 * Accesses all necessary state via hooks internally.
 */
export const StandardQuestionnaireActions = ({
  ref,
  ...props
}: StandardQuestionnaireActionsProps) => {
  const { submitActionLabel, nextActionLabel } = props;

  // Access state via hooks
  const { up } = useBreakpoint();
  const isDesktop = up("md");
  const { errors } = useQuestionnaire();
  const sections = useSections();
  const { isFirstSection, isLastSection, currentSection } = useCurrentSection();
  const shouldBlockSubmission = useShouldBlockSubmission();

  // Computed values
  const isEditSection = currentSection?.displayAs === "edit";
  // If questionnaire has ReviewSection, only ReviewSection should show submit (it has its own)
  const hasReviewSection = sections.some(s => s.displayAs === "review");
  const showSubmitAction = isLastSection && !hasReviewSection && !isEditSection;
  const shouldShowBackAction = !isFirstSection && !isDesktop;
  const shouldShowNextAction = !isLastSection && !showSubmitAction;

  return (
    <QuestionnaireActions ref={ref}>
      {shouldShowBackAction && <QuestionnaireActions.Back />}
      {showSubmitAction && (
        <QuestionnaireActions.Submit
          buttonLabel={submitActionLabel}
          isDisabled={shouldBlockSubmission}
          errors={errors}
        />
      )}
      {shouldShowNextAction && (
        <QuestionnaireActions.Next label={nextActionLabel} />
      )}
    </QuestionnaireActions>
  );
};

StandardQuestionnaireActions.displayName = "StandardQuestionnaireActions";
